import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { APP_NAME } from '../lib/appConfig';
import { api, ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { errorMessage } from '../lib/errors';
import { redirectTo } from '../lib/nav';
import { Button } from '../components/ui/button';
import { CheckIcon } from '../components/icons';
import BrandBackdrop from '../components/brand/BrandBackdrop';
import Eyebrow from '../components/brand/Eyebrow';
import { cn } from '../lib/cn';

// CHANGE_02: the four-tier pricing page (Free / Pro / Team / Enterprise). Mirrors
// the plan DATA (CHANGE_01) and makes the version-control value ladder explicit —
// Free never paywalls "don't lose your work" (versioning + restore + basic diff);
// tiers differentiate on depth (history), power (advanced diff + branching), and
// collaboration (merge + seats + governance). Self-serve checkout for Pro/Team
// arrives with billing (E2); until then the CTA is an honest "coming soon".

type TierId = 'free' | 'pro' | 'team' | 'enterprise';

interface Tier {
  id: TierId;
  name: string;
  blurb: string;
  monthly: number | null; // null = custom / no price
  annual: number | null; // per-month equivalent, billed annually
  highlight?: boolean;
  cta: string;
  features: string[];
  note?: string;
}

const TIERS: Tier[] = [
  {
    id: 'free',
    name: 'Free',
    blurb: 'Genuinely generous — never lose your work.',
    monthly: 0,
    annual: 0,
    cta: 'Get started',
    features: [
      'Unlimited drawing & viewing',
      '1 active project',
      'Up to 2 collaborators (view + comment)',
      'Anonymous commenting',
      '2 active share links · 5 exports/mo',
      '100 MB storage',
      '30-day version history + restore',
      'Basic “what changed” diff',
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    blurb: 'The power individual.',
    monthly: 7,
    annual: 5,
    highlight: true,
    cta: 'Choose Pro',
    features: [
      'Unlimited projects, exports & share links',
      'Unlimited collaborators with edit access',
      'Full version history (kept forever)',
      'Advanced visual diff + change tracking',
      'Branching',
      '5 GB storage',
      'Priority support',
    ],
  },
  {
    id: 'team',
    name: 'Team',
    blurb: 'Collaboration for a whole team.',
    monthly: 30,
    annual: 24,
    cta: 'Choose Team',
    features: [
      'Everything in Pro',
      'Up to 10 seats',
      '20 GB shared storage',
      'Assisted branch merge (coming soon)',
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    blurb: 'Governance at scale.',
    monthly: null,
    annual: null,
    cta: 'Contact us',
    features: [
      'Everything in Team',
      'Configurable retention policy',
      'SSO + audit export',
      'Negotiated seats & storage',
    ],
  },
];

function priceLabel(tier: Tier, annual: boolean): string {
  if (tier.monthly === null) return 'Custom';
  if (tier.monthly === 0) return 'Free';
  const n = annual ? tier.annual : tier.monthly;
  return `$${n}/mo`;
}

export default function PlansPage() {
  const navigate = useNavigate();
  const { me, markOnboarded } = useAuth();
  const [annual, setAnnual] = useState(true); // annual default (spec)
  const [pending, setPending] = useState<TierId | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [finishing, setFinishing] = useState(false);

  // Welcome mode: the one-time first-run plan step — a signed-in user who hasn't
  // completed onboarding. Picking ANY plan (including staying on Free, or skipping)
  // completes it, so the NEXT login lands editor-first instead of back here.
  const onboarding = me?.onboarded === false;

  // Complete onboarding (best-effort) and continue into the app (→ editor-first).
  async function finishOnboarding() {
    if (finishing) return;
    setFinishing(true);
    try {
      await markOnboarded();
    } catch {
      // Non-fatal: the flag is a UX hint, never a wall. Worst case the user sees
      // this step again next login — never block them from entering the app.
    }
    navigate('/', { replace: true });
  }

  // Self-serve checkout: create the Stripe Checkout Session, then hand the browser
  // off to its URL. The subscription does NOT flip here — the verified webhook does
  // that, and /settings shows the honest pending→active state on return.
  async function choose(tier: TierId) {
    setError(null);
    setPending(tier);
    // In welcome mode, complete onboarding BEFORE handing off to Stripe — so
    // returning from a cancelled or completed checkout doesn't bounce the user back
    // into this step (the landing gate keys off the now-set flag).
    if (onboarding) {
      try {
        await markOnboarded();
      } catch {
        /* best-effort — see finishOnboarding */
      }
    }
    try {
      const { url } = await api.startCheckout(tier, annual ? 'annual' : 'monthly');
      redirectTo(url);
    } catch (err) {
      setPending(null);
      if (err instanceof ApiError && err.status === 401) {
        navigate('/login'); // must sign in before paying
        return;
      }
      if (err instanceof ApiError && err.status === 503) {
        setError('Self-serve checkout isn’t available yet — please check back soon.');
        return;
      }
      setError(errorMessage(err, 'Could not start checkout. Please try again.'));
    }
  }

  return (
    <div className="relative min-h-full overflow-y-auto">
      <BrandBackdrop />

      <div className="mx-auto max-w-6xl px-6 py-16 sm:px-8 sm:py-20">
        <div className="mx-auto max-w-2xl text-center">
          <Eyebrow className="justify-center">{onboarding ? 'Welcome' : 'Pricing'}</Eyebrow>
          <h1 className="mt-6 text-balance brand-h1 text-foreground">
            {onboarding ? `Welcome to ${APP_NAME} — choose a plan` : `${APP_NAME} plans`}
          </h1>
          <p className="mt-5 text-pretty text-base leading-relaxed text-muted-foreground sm:text-lg">
            Free to start, no credit card — you keep your work and your version history. Paid plans
            add full history, advanced diff, branching and team collaboration.
          </p>

          {/* First-run welcome affordance: skip the plan step and start on Free. */}
          {onboarding ? (
            <div className="mt-5">
              <button
                type="button"
                onClick={() => void finishOnboarding()}
                disabled={finishing}
                className="text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline disabled:opacity-60"
              >
                {finishing ? 'Setting up your workspace…' : 'Skip for now — continue with Free →'}
              </button>
            </div>
          ) : null}

          {/* interval toggle — ink active pill, like the landing site */}
          <div className="mt-7 flex flex-col items-center gap-2">
            <div
              className="inline-flex items-center rounded-md border border-border bg-card p-1 text-sm"
              role="group"
              aria-label="Billing interval"
            >
              {(
                [
                  ['Monthly', false],
                  ['Annual', true],
                ] as const
              ).map(([label, isAnnual]) => (
                <button
                  key={label}
                  type="button"
                  aria-pressed={annual === isAnnual}
                  onClick={() => setAnnual(isAnnual)}
                  className={cn(
                    'rounded-sm px-3.5 py-1.5 font-medium transition-colors',
                    annual === isAnnual
                      ? 'bg-foreground text-background'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
            <p className="font-mono text-xs text-muted-foreground">
              {annual ? 'Save up to 29% billed annually' : 'Switch to annual to save'}
            </p>
          </div>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          {TIERS.map((tier) => {
            const paid = tier.monthly !== null && tier.monthly > 0;
            return (
              <div
                key={tier.id}
                className={cn(
                  'relative flex flex-col rounded-lg border bg-card p-6',
                  tier.highlight ? 'border-primary' : 'border-border',
                )}
                style={tier.highlight ? { boxShadow: 'var(--brand-shadow-panel)' } : undefined}
              >
                <div className="flex items-center justify-between">
                  <h2 className="font-display text-xl font-semibold text-foreground">{tier.name}</h2>
                  {tier.highlight ? (
                    <span className="brand-eyebrow rounded-full bg-[color-mix(in_srgb,var(--cad-accent)_14%,transparent)] px-2.5 py-0.5 text-[10px] text-primary">
                      Popular
                    </span>
                  ) : null}
                </div>
                <p className="mt-1.5 min-h-[2.5em] text-sm text-muted-foreground">{tier.blurb}</p>

                <div className="mt-4 flex items-baseline gap-1.5">
                  <span className="font-display text-4xl font-semibold text-foreground">
                    {priceLabel(tier, annual)}
                  </span>
                  {paid ? <span className="font-mono text-sm text-muted-foreground">/mo</span> : null}
                </div>
                <p className="mt-1 h-4 font-mono text-[11px] text-muted-foreground">
                  {paid && annual ? 'billed annually' : ''}
                </p>

                <div className="mt-5">
                  {tier.id === 'free' ? (
                    onboarding ? (
                      <Button
                        variant="outline"
                        className="h-11 w-full"
                        onClick={() => void finishOnboarding()}
                        disabled={finishing}
                      >
                        {finishing ? 'Starting…' : 'Start with Free'}
                      </Button>
                    ) : (
                      <Button asChild variant="outline" className="h-11 w-full">
                        <Link to="/dashboard">{tier.cta}</Link>
                      </Button>
                    )
                  ) : tier.id === 'enterprise' ? (
                    <Button asChild variant="outline" className="h-11 w-full">
                      <a href="mailto:sales@branchcad.com?subject=BranchCAD%20Enterprise">{tier.cta}</a>
                    </Button>
                  ) : (
                    <Button
                      className="h-11 w-full"
                      variant={tier.highlight ? 'default' : 'outline'}
                      onClick={() => choose(tier.id)}
                      disabled={pending !== null}
                    >
                      {pending === tier.id ? 'Redirecting…' : tier.cta}
                    </Button>
                  )}
                </div>

                <ul className="mt-6 flex flex-col gap-2.5 border-t border-border pt-5 text-sm text-foreground">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5">
                      <CheckIcon aria-hidden className="mt-0.5 size-4 shrink-0 text-primary" />
                      <span className="text-pretty">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {error ? (
          <p className="mt-6 text-center text-sm text-destructive" role="alert">
            {error}
          </p>
        ) : null}

        <p className="mt-8 text-center font-mono text-xs text-muted-foreground">
          Every shared project link supports up to 20 concurrent participants, on all plans.
        </p>
      </div>
    </div>
  );
}
